import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { resetPassword } from "../../services/authService";

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  padding: 2.5rem 2.2rem 2.2rem 2.2rem;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e97d7dff;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 1.5rem;
  text-align: center;
  line-height: 1.5;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #444;
  margin-bottom: 0.3rem;
  display: block;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${(p) => (p.error ? "#e97d7dff" : "#e0e0e0")};
  font-size: 1rem;
  margin-bottom: 0.7rem;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    border-color: #e97d7dff;
    outline: none;
  }
`;

const ErrorMsg = styled.div`
  color: #e97d7dff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
`;

const SuccessMsg = styled.div`
  color: #28a745;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  padding: 1rem;
  background: #d4edda;
  border-radius: 0.5rem;
  border: 1px solid #c3e6cb;
`;

const Button = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  margin-top: 0.7rem;
  margin-bottom: 0.7rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Link = styled.a`
  color: #e97d7dff;
  font-size: 0.98rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #000;
  }
`;

const BottomText = styled.div`
  text-align: center;
  font-size: 1rem;
  margin-top: 1.2rem;
  color: #444;
`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    if (!emailRegex.test(email)) {
      setError("Invalid email format.");
      return false;
    }
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError("");
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!validate()) return;

    setLoading(true);
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      console.error("Password reset error:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);

      if (err.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else {
        setError(
          `Failed to send reset email: ${err.message || "Please try again."}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card as="form" onSubmit={handleSubmit} autoComplete="off">
        <Title>Forgot Password?</Title>
        <Description>
          Enter your email address and we'll send you a link to reset your
          password.
        </Description>
        {success ? (
          <>
            <SuccessMsg>
              <strong>Check your email!</strong>
              <br />
              We've sent a password reset link to <strong>{email}</strong>
              <br />
              <br />
              Please check your inbox and click the link to reset your password.
              The link will expire in 1 hour.
            </SuccessMsg>
            <Button type="button" onClick={() => navigate("/signin")}>
              Back to Sign In
            </Button>
          </>
        ) : (
          <>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={handleChange}
              error={!!error}
              autoComplete="email"
              required
              placeholder="Enter your email address"
            />
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <Button type="submit" disabled={loading || !email}>
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
            <BottomText>
              Remember your password?{" "}
              <Link
                as="button"
                type="button"
                onClick={() => navigate("/signin")}
              >
                Sign In
              </Link>
            </BottomText>
          </>
        )}
      </Card>
    </Wrapper>
  );
};

export default ForgotPassword;
